import { useEffect, useState } from "react";
import { Loader2, Play, ShieldAlert, RotateCcw, Save, Gavel } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { fmtP } from "@/lib/jackpot";

const fmtDate = (s) => new Date(s).toLocaleString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const PH = { none: "Kein Event", upcoming: "Geplant", live: "LIVE", closing: "Wartet auf Auszahlung", bankrupt: "BANKROTT", settled: "Beendet" };
const ST = { active: "aktiv", bankrupt: "Bankrott", paid: "ausgeschüttet", carried: "übertragen" };
const KL = { plus: "+1.000", minus: "−1.000", double: "×2", halve: "÷2 (halbieren)", bankrupt: "Bankrott" };
const card = "bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-3";
const inp = "h-10 px-3 rounded-full border border-pink-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-rose-300";
const btn = "h-10 px-5 rounded-full text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50";

function ConfigForm({ cfg, onSaved }) {
  const [f, setF] = useState(cfg);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setP = (k, v) => setF((p) => ({ ...p, probs: { ...p.probs, [k]: v } }));
  const sum = Object.values(f.probs).reduce((a, b) => a + Number(b || 0), 0);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/jackpot/admin/config", { enabled: f.enabled, paused: f.paused, start_amount: Number(f.start_amount), cooldown_sec: Number(f.cooldown_sec), one_per_device: f.one_per_device,
        probs: Object.fromEntries(Object.entries(f.probs).map(([k, v]) => [k, Number(v)])) });
      toast.success("Einstellungen gespeichert"); onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid="jr-admin-config">
      <p className="font-bold">Einstellungen</p>
      <div className="flex flex-wrap gap-4 text-sm">
        {[["enabled", "Aktiviert"], ["paused", "Pausiert"], ["one_per_device", "Nur 1 Konto pro Gerät"]].map(([k, l]) => (
          <label key={k} className="inline-flex items-center gap-2"><input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`jr-admin-${k}`} className="accent-rose-500 w-4 h-4" />{l}</label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs font-semibold text-slate-500">
        <label>Jackpot nach Neustart (Bankrott)<input type="number" value={f.start_amount} onChange={(e) => set("start_amount", e.target.value)} data-testid="jr-admin-start-amount" className={inp} /></label>
        <label>Wartezeit zwischen Drehs (Sek.)<input type="number" value={f.cooldown_sec} onChange={(e) => set("cooldown_sec", e.target.value)} data-testid="jr-admin-cooldown" className={inp} /></label>
      </div>
      <p className="text-xs font-semibold text-slate-500">Wahrscheinlichkeiten in % (Summe {sum.toFixed(1)} %)</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end text-xs font-semibold text-slate-500">
        {Object.keys(KL).map((k) => <label key={k}>{KL[k]}<input type="number" step="0.1" value={f.probs[k]} onChange={(e) => setP(k, e.target.value)} data-testid={`jr-admin-prob-${k}`} className={inp} /></label>)}
      </div>
      <button onClick={save} disabled={busy || Math.abs(sum - 100) > 0.001} data-testid="jr-admin-save" className={`${btn} bg-rose-500 text-white`}><Save className="w-4 h-4" /> Speichern</button>
    </div>
  );
}

function VideoControl({ d, reload }) {
  const toLocal = (s) => { const t = new Date(s); return new Date(t.getTime() - t.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
  const [on, setOn] = useState(d.video.enabled);
  const [from, setFrom] = useState(toLocal(d.video.from));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/jackpot/admin/video", { enabled: on, video_from: new Date(from).toISOString() }); toast.success("Werbevideo im Feed gespeichert"); reload(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-rose-50 p-4 space-y-2" data-testid="jr-admin-video">
      <p className="text-sm font-bold">Werbevideo oben im Feed</p>
      <p className="text-xs text-slate-500">Sichtbar ab {fmtDate(d.video.from)} bis Event-Ende ({fmtDate(d.round.ends_at)}), deutsche Zeit. Bei Bankrott verschwindet es.</p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} data-testid="jr-admin-video-enabled" className="accent-rose-500 w-4 h-4" /> Anzeigen</label>
        <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="jr-admin-video-from" className={`${inp} !w-56`} />
        <button onClick={save} disabled={busy || !from} data-testid="jr-admin-video-save" className={`${btn} bg-rose-500 text-white`}><Save className="w-4 h-4" /> Speichern</button>
      </div>
    </div>
  );
}

function Actions({ d, reload }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); setReason(""); setAmount(""); reload(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); } };
  const canStart = !d.round || d.round.id !== d.next.id;
  return (
    <div className={card} data-testid="jr-admin-actions">
      <p className="font-bold">Event steuern</p>
      {canStart && (
        <div className="rounded-2xl bg-rose-50 p-4 space-y-2">
          <p className="text-sm">Nächstes Wochenende: <b>{fmtDate(d.next.starts_at)}</b> bis <b>{fmtDate(d.next.ends_at)}</b>{d.carry > 0 && <> · Übertrag <b>{fmtP(d.carry)}</b> Punkte</>}</p>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="jr-admin-notify" className="accent-rose-500 w-4 h-4" /> Push an alle Mitglieder senden</label>
          <div><button disabled={busy} onClick={() => window.confirm("Jackpot Roulette für dieses Wochenende starten?") && run(() => api.post("/jackpot/admin/start", { notify }), "Event gestartet")} data-testid="jr-admin-start" className={`${btn} pink-gradient text-white`}><Play className="w-4 h-4" /> Event starten</button></div>
        </div>
      )}
      {d.round && d.round.status === "active" && !d.round.settled && (
        <div className="flex flex-wrap gap-2 items-end">
          <input type="number" placeholder="Neuer Jackpot" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="jr-admin-correct-amount" className={`${inp} !w-36`} />
          <input placeholder="Grund (Pflicht)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="jr-admin-reason" className={`${inp} !w-56`} />
          <button disabled={busy || !amount || reason.trim().length < 3} onClick={() => run(() => api.post("/jackpot/admin/correct", { amount: Number(amount), reason }), "Jackpot korrigiert")} data-testid="jr-admin-correct" className={`${btn} border border-pink-200 text-rose-600`}>Jackpot korrigieren</button>
        </div>
      )}
      {d.phase === "bankrupt" && (
        <div className="flex flex-wrap gap-2 items-end">
          <input placeholder="Grund für Neustart" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="jr-admin-restart-reason" className={`${inp} !w-56`} />
          <button disabled={busy || reason.trim().length < 3} onClick={() => run(() => api.post("/jackpot/admin/restart", { reason }), "Event neu gestartet")} data-testid="jr-admin-restart" className={`${btn} bg-slate-900 text-white`}><RotateCcw className="w-4 h-4" /> Nach Bankrott neu starten</button>
        </div>
      )}
      {d.phase === "closing" && <button disabled={busy} onClick={() => run(() => api.post("/jackpot/admin/settle"), "Auszahlung ausgeführt")} data-testid="jr-admin-settle" className={`${btn} bg-amber-400 text-amber-950`}><Gavel className="w-4 h-4" /> Jetzt auszahlen</button>}
      {d.round && d.video && !d.round.settled && <VideoControl key={d.video.from + d.video.enabled} d={d} reload={reload} />}
    </div>
  );
}

function Status({ d }) {
  const r = d.round;
  const safe = d.safe_until && new Date() < new Date(d.safe_until);
  return (
    <div className={card} data-testid="jr-admin-status">
      <div className="flex flex-wrap items-center gap-2"><p className="font-bold">Status</p><span className="px-2.5 h-6 inline-flex items-center rounded-full bg-rose-100 text-rose-700 text-xs font-extrabold" data-testid="jr-admin-phase">{PH[d.phase]}</span></div>
      {r ? <>
        <p className="text-4xl font-extrabold text-rose-600" data-testid="jr-admin-amount">{fmtP(r.amount)} <span className="text-base text-slate-500">Punkte</span></p>
        <p className="text-sm text-slate-500">{fmtDate(r.starts_at)} – {fmtDate(r.ends_at)} · Lotto-Ziehung {new Date(r.draw_week).toLocaleDateString("de-DE")} 20:00 · {fmtP(r.spins)} Drehs · {fmtP(r.players)} Mitspieler · Höchststand {fmtP(r.peak)}</p>
        <div className={`rounded-2xl p-3 text-sm flex gap-2 ${safe ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-600"}`} data-testid="jr-admin-secret">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <span><b>Geheimregel (nur für Admins sichtbar):</b> Bankrott ist bis {fmtDate(d.safe_until)} gesperrt (0 %) – {safe ? "gerade AKTIV" : "abgelaufen, Bankrott ist jetzt möglich"}. Aktuell gültig: {Object.entries(d.live_probs).map(([k, v]) => `${KL[k]} ${v.toFixed(1)} %`).join(" · ")}</span>
        </div>
      </> : <p className="text-sm text-slate-500">Noch kein Event gestartet.</p>}
    </div>
  );
}

function History({ d }) {
  return (
    <div className={card} data-testid="jr-admin-history">
      <p className="font-bold">Verlauf</p>
      {d.history.length === 0 ? <p className="text-sm text-slate-400">Noch keine Events.</p> : d.history.map((h) => (
        <div key={h.id} className="flex flex-wrap justify-between gap-2 text-sm border-b border-pink-50 pb-2">
          <span className="font-semibold">{h.id.replace("WE-", "Wochenende ")}</span>
          <span className="text-slate-500">{ST[h.status]} · {fmtP(h.final_amount ?? h.amount)} P.{h.payout?.winners?.length ? ` · ${h.payout.winners.map((w) => w.name).join(", ")}` : ""}</span>
        </div>
      ))}
      {d.spins.length > 0 && <>
        <p className="font-bold pt-2">Letzte Drehs</p>
        <div className="max-h-72 overflow-y-auto space-y-1">{d.spins.map((s) => <p key={s.id} className="text-xs text-slate-600">#{s.seq} {s.user.name} · {KL[s.result]} · {fmtP(s.before)} → {fmtP(s.after)}</p>)}</div>
      </>}
      {d.suspicious.length > 0 && <p className="text-xs text-amber-700" data-testid="jr-admin-suspicious">Auffällig: {d.suspicious.map((g) => `${g.via}: ${g.users.map((u) => u.name).join(", ")}`).join(" · ")}</p>}
    </div>
  );
}

export function JackpotRouletteAdmin() {
  const [d, setD] = useState(null);
  const load = () => api.get("/jackpot/admin/overview").then(({ data }) => setD(data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  return (
    <div className="grid xl:grid-cols-2 gap-4 max-w-6xl" data-testid="jr-admin">
      <div className="space-y-4"><Status d={d} /><Actions d={d} reload={load} /></div>
      <div className="space-y-4"><ConfigForm key={JSON.stringify(d.config)} cfg={d.config} onSaved={load} /><History d={d} /></div>
    </div>
  );
}
